/**
 * Volunteer Management - Full HOS Integration
 * 
 * Comprehensive volunteer management treating volunteers as a core hospice discipline.
 * Includes profiles, compliance tracking, patient assignments, IDT integration,
 * visit tracking, specialty programs, and leadership dashboards.
 * 
 * REFACTORED: Tab components extracted to /components/volunteer/
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users, Heart, UserPlus, ClipboardCheck, Calendar, Clock,
  CheckCircle, AlertTriangle, RefreshCw, Loader2, Search,
  Phone, Mail, FileText, ChevronRight, ChevronLeft, Eye,
  Shield, Star, Music, Dog, Car, Bed, Activity, TrendingUp,
  AlertCircle, Filter, BarChart3, Download, Plus, Edit, X
} from "lucide-react";
import { toast } from "sonner";

// Import extracted tab components
import { 
  VolunteerDashboardTab, 
  VolunteerRosterTab, 
  ComplianceTrackerTab,
  FivePercentReportTab,
  AssignmentsTab,
  VisitsTab,
  IDTSummariesTab,
  ProgramsTab,
  RecruitmentTab
} from "@/components/volunteer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configurations
const STATUS_CONFIG = {
  recruiting: { label: "Recruiting", color: "bg-blue-500", textColor: "text-blue-400", bgLight: "bg-blue-500/20" },
  onboarding: { label: "Onboarding", color: "bg-yellow-500", textColor: "text-yellow-400", bgLight: "bg-yellow-500/20" },
  active: { label: "Active", color: "bg-green-500", textColor: "text-green-400", bgLight: "bg-green-500/20" },
  on_hold: { label: "On Hold", color: "bg-orange-500", textColor: "text-orange-400", bgLight: "bg-orange-500/20" },
  inactive: { label: "Inactive", color: "bg-gray-500", textColor: "text-gray-400", bgLight: "bg-gray-500/20" }
};

const ROLE_TYPES = {
  companionship: { label: "Companionship", icon: Heart },
  bereavement: { label: "Bereavement Support", icon: Heart },
  threshold_singers: { label: "Threshold Singers", icon: Music },
  pet_therapy: { label: "Pet Therapy", icon: Dog },
  administrative: { label: "Administrative", icon: ClipboardCheck },
  transportation: { label: "Transportation", icon: Car },
  respite: { label: "Respite Care", icon: Bed }
};

const COMPLIANCE_CONFIG = {
  complete: { label: "Complete", color: "text-green-400", icon: CheckCircle },
  incomplete: { label: "Incomplete", color: "text-yellow-400", icon: AlertTriangle },
  expired: { label: "Expired", color: "text-red-400", icon: AlertCircle },
  not_started: { label: "Not Started", color: "text-gray-400", icon: Clock }
};

export default function VolunteerManagement() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [volunteers, setVolunteers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [visits, setVisits] = useState([]);
  const [complianceTracker, setComplianceTracker] = useState(null);
  const [missedVisits, setMissedVisits] = useState([]);
  const [weeklyCheckins, setWeeklyCheckins] = useState([]);
  const [idtSummaries, setIdtSummaries] = useState([]);
  const [specialtyPrograms, setSpecialtyPrograms] = useState([]);
  const [fivePercentReport, setFivePercentReport] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [volunteerRequests, setVolunteerRequests] = useState([]);
  const [orientationModules, setOrientationModules] = useState([]);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialog states
  const [volunteerDialog, setVolunteerDialog] = useState(false);
  const [volunteerDetailDialog, setVolunteerDetailDialog] = useState(false);
  const [assignmentDialog, setAssignmentDialog] = useState(false);
  const [visitDialog, setVisitDialog] = useState(false);
  const [complianceDialog, setComplianceDialog] = useState(false);
  const [idtSummaryDialog, setIdtSummaryDialog] = useState(false);
  const [inquiryDialog, setInquiryDialog] = useState(false);
  const [interviewDialog, setInterviewDialog] = useState(false);
  
  // Selected items
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  const [selectedComplianceItem, setSelectedComplianceItem] = useState(null);
  
  // Form states
  const [volunteerForm, setVolunteerForm] = useState({
    name: "", email: "", phone: "", address: "", city: "", state: "", zip_code: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    role_types: ["companionship"], skills: "", languages: "english",
    preferred_schedule: "", notes: ""
  });
  
  const [inquiryForm, setInquiryForm] = useState({
    name: "", email: "", phone: "", interest_areas: [], how_heard: "", notes: ""
  });
  
  const [interviewForm, setInterviewForm] = useState({
    volunteer_id: "", interview_date: "", interview_time: "", interviewer_name: "", location: "", notes: ""
  });
  
  const [assignmentForm, setAssignmentForm] = useState({
    volunteer_id: "", patient_id: "", patient_name: "", requested_by: "",
    location: "", schedule: "", services_needed: [], psychosocial_notes: "", special_instructions: ""
  });
  
  const [visitForm, setVisitForm] = useState({
    volunteer_id: "", patient_id: "", visit_date: new Date().toISOString().split('T')[0],
    start_time: "", end_time: "", duration_minutes: 60,
    activities: [], observations: "", concerns: "", notes: ""
  });

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/dashboard`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  }, []);

  // Fetch volunteers
  const fetchVolunteers = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      let url = `${API_URL}/api/volunteer-management/volunteers?limit=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await fetch(url, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVolunteers(data.volunteers || []);
      }
    } catch (error) {
      console.error("Error fetching volunteers:", error);
    }
  }, [statusFilter, searchQuery]);

  // Fetch assignments
  const fetchAssignments = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/assignments?limit=100`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
    }
  }, []);

  // Fetch visits
  const fetchVisits = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/visits?limit=50`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVisits(data.visits || []);
      }
    } catch (error) {
      console.error("Error fetching visits:", error);
    }
  }, []);

  // Fetch compliance tracker
  const fetchComplianceTracker = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/compliance/tracker?expiring_soon=true`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setComplianceTracker(data);
      }
    } catch (error) {
      console.error("Error fetching compliance tracker:", error);
    }
  }, []);

  // Fetch missed visits
  const fetchMissedVisits = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/visits/missed?days_back=7`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMissedVisits(data.missed_visits || []);
      }
    } catch (error) {
      console.error("Error fetching missed visits:", error);
    }
  }, []);

  // Fetch weekly check-ins
  const fetchWeeklyCheckins = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/check-ins/weekly-list`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWeeklyCheckins(data.check_in_list || []);
      }
    } catch (error) {
      console.error("Error fetching weekly check-ins:", error);
    }
  }, []);

  // Fetch IDT summaries
  const fetchIdtSummaries = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/idt/summaries?status=pending_review`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIdtSummaries(data.summaries || []);
      }
    } catch (error) {
      console.error("Error fetching IDT summaries:", error);
    }
  }, []);

  // Fetch specialty programs
  const fetchSpecialtyPrograms = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/specialty-programs`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSpecialtyPrograms(data.programs || []);
      }
    } catch (error) {
      console.error("Error fetching specialty programs:", error);
    }
  }, []);

  // Fetch 5% Rule Report
  const fetchFivePercentReport = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/reports/five-percent-rule`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFivePercentReport(data);
      }
    } catch (error) {
      console.error("Error fetching 5% report:", error);
    }
  }, []);

  // Fetch Inquiries
  const fetchInquiries = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/recruitment/inquiries`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInquiries(data.inquiries || []);
      }
    } catch (error) {
      console.error("Error fetching inquiries:", error);
    }
  }, []);

  // Fetch Interviews
  const fetchInterviews = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/recruitment/interviews`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInterviews(data.interviews || []);
      }
    } catch (error) {
      console.error("Error fetching interviews:", error);
    }
  }, []);

  // Fetch Volunteer Requests
  const fetchVolunteerRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/requests?status=pending`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVolunteerRequests(data.requests || []);
      }
    } catch (error) {
      console.error("Error fetching volunteer requests:", error);
    }
  }, []);

  // Fetch Orientation Modules
  const fetchOrientationModules = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/onboarding/modules`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrientationModules(data.modules || []);
      }
    } catch (error) {
      console.error("Error fetching orientation modules:", error);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchVolunteers(),
        fetchAssignments(),
        fetchVisits(),
        fetchComplianceTracker(),
        fetchMissedVisits(),
        fetchWeeklyCheckins(),
        fetchIdtSummaries(),
        fetchSpecialtyPrograms(),
        fetchFivePercentReport(),
        fetchInquiries(),
        fetchInterviews(),
        fetchVolunteerRequests(),
        fetchOrientationModules()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchVolunteers, fetchAssignments, fetchVisits, fetchComplianceTracker, fetchMissedVisits, fetchWeeklyCheckins, fetchIdtSummaries, fetchSpecialtyPrograms, fetchFivePercentReport, fetchInquiries, fetchInterviews, fetchVolunteerRequests, fetchOrientationModules]);

  // Refetch volunteers when filters change
  useEffect(() => {
    fetchVolunteers();
  }, [statusFilter, searchQuery, fetchVolunteers]);

  // Create volunteer
  const handleCreateVolunteer = async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const payload = {
        ...volunteerForm,
        skills: volunteerForm.skills.split(",").map(s => s.trim()).filter(Boolean),
        languages: volunteerForm.languages.split(",").map(l => l.trim()).filter(Boolean)
      };
      
      const res = await fetch(`${API_URL}/api/volunteer-management/volunteers`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success("Volunteer created successfully");
        setVolunteerDialog(false);
        setVolunteerForm({
          name: "", email: "", phone: "", address: "", city: "", state: "", zip_code: "",
          emergency_contact_name: "", emergency_contact_phone: "",
          role_types: ["companionship"], skills: "", languages: "english",
          preferred_schedule: "", notes: ""
        });
        fetchVolunteers();
        fetchDashboard();
      } else {
        toast.error("Failed to create volunteer");
      }
    } catch (error) {
      console.error("Error creating volunteer:", error);
      toast.error("Failed to create volunteer");
    }
  };

  // View volunteer details
  const handleViewVolunteer = async (volunteerId) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/volunteers/${volunteerId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedVolunteer(data.volunteer);
        setVolunteerDetailDialog(true);
      }
    } catch (error) {
      console.error("Error fetching volunteer details:", error);
    }
  };

  // Update volunteer status
  const handleUpdateStatus = async (volunteerId, newStatus) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/volunteers/${volunteerId}/status`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ new_status: newStatus })
      });
      
      if (res.ok) {
        toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
        fetchVolunteers();
        fetchDashboard();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  // Log visit
  const handleLogVisit = async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/visits`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(visitForm)
      });
      
      if (res.ok) {
        toast.success("Visit logged successfully");
        setVisitDialog(false);
        setVisitForm({
          volunteer_id: "", patient_id: "", visit_date: new Date().toISOString().split('T')[0],
          start_time: "", end_time: "", duration_minutes: 60,
          activities: [], observations: "", concerns: "", notes: ""
        });
        fetchVisits();
        fetchDashboard();
      }
    } catch (error) {
      console.error("Error logging visit:", error);
      toast.error("Failed to log visit");
    }
  };

  // Export 5% Rule Report
  const handleExportReport = async (format) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const today = new Date();
      const startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      const endDate = today.toISOString().split('T')[0];
      
      const url = `${API_URL}/api/volunteer-management/reports/export?report_type=five_percent&format=${format}&start_date=${startDate}&end_date=${endDate}`;
      
      const res = await fetch(url, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (res.ok) {
        if (format === 'json') {
          const data = await res.json();
          const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = downloadUrl;
          a.download = data.filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          // For PDF, Excel, CSV - download directly
          const blob = await res.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = downloadUrl;
          const extension = format === 'excel' ? 'xlsx' : format;
          a.download = `volunteer_five_percent_${startDate}_to_${endDate}.${extension}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        }
        toast.success(`Report exported as ${format.toUpperCase()}`);
      } else {
        toast.error("Failed to export report");
      }
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error("Failed to export report");
    }
  };

  // Refresh all data
  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(),
      fetchVolunteers(),
      fetchAssignments(),
      fetchVisits(),
      fetchComplianceTracker(),
      fetchMissedVisits(),
      fetchWeeklyCheckins(),
      fetchIdtSummaries()
    ]);
    setLoading(false);
    toast.success("Data refreshed");
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.inactive;
    return (
      <Badge className={`${config.bgLight} ${config.textColor} border-0`}>
        {config.label}
      </Badge>
    );
  };

  // Render compliance badge
  const renderComplianceBadge = (status) => {
    const config = COMPLIANCE_CONFIG[status] || COMPLIANCE_CONFIG.not_started;
    const Icon = config.icon;
    return (
      <div className={`flex items-center gap-1 ${config.color}`}>
        <Icon className="w-4 h-4" />
        <span className="text-sm">{config.label}</span>
      </div>
    );
  };

  if (loading && !dashboard) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? "bg-slate-950" : "bg-gray-50"}`}>
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-white" : "bg-gray-50 text-gray-900"}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"} px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-button"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Users className="w-6 h-6 text-teal-500" />
                Volunteer Management
              </h1>
              <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                HOS Integrated Volunteer Discipline Workflow
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => setVolunteerDialog(true)}
              data-testid="add-volunteer-button"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Volunteer
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`mb-6 flex-wrap ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
            <TabsTrigger value="dashboard" data-testid="dashboard-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="volunteers" data-testid="volunteers-tab">
              <Users className="w-4 h-4 mr-2" />
              Volunteers
            </TabsTrigger>
            <TabsTrigger value="recruitment" data-testid="recruitment-tab">
              <UserPlus className="w-4 h-4 mr-2" />
              Recruitment
            </TabsTrigger>
            <TabsTrigger value="compliance" data-testid="compliance-tab">
              <Shield className="w-4 h-4 mr-2" />
              Compliance
            </TabsTrigger>
            <TabsTrigger value="assignments" data-testid="assignments-tab">
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Assignments
            </TabsTrigger>
            <TabsTrigger value="visits" data-testid="visits-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Visits
            </TabsTrigger>
            <TabsTrigger value="idt" data-testid="idt-tab">
              <FileText className="w-4 h-4 mr-2" />
              IDT
            </TabsTrigger>
            <TabsTrigger value="five-percent" data-testid="five-percent-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              5% Rule
            </TabsTrigger>
            <TabsTrigger value="programs" data-testid="programs-tab">
              <Star className="w-4 h-4 mr-2" />
              Programs
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab - Using Extracted Component */}
          <TabsContent value="dashboard" className="space-y-6">
            <VolunteerDashboardTab
              isDark={isDark}
              dashboard={dashboard}
              missedVisits={missedVisits}
              weeklyCheckins={weeklyCheckins}
              idtSummaries={idtSummaries}
              visits={visits}
              setActiveTab={setActiveTab}
            />
          </TabsContent>

          {/* Volunteers Tab */}
          <TabsContent value="volunteers" className="space-y-4">
            {/* Filters */}
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
                      <Input
                        placeholder="Search volunteers..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`pl-10 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                        data-testid="volunteer-search"
                      />
                    </div>
                  </div>
                  <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
                    <SelectTrigger className={`w-[180px] ${isDark ? "bg-slate-800 border-slate-700" : ""}`}>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Volunteers List */}
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Role Types</TableHead>
                      <TableHead>Compliance</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Visits</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {volunteers.map((volunteer) => (
                      <TableRow 
                        key={volunteer.id} 
                        className={isDark ? "border-slate-800" : ""}
                        data-testid={`volunteer-row-${volunteer.id}`}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium">{volunteer.name}</p>
                            <p className={`text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                              {volunteer.email || volunteer.phone || "No contact"}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>{renderStatusBadge(volunteer.status)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(volunteer.role_types || []).slice(0, 2).map((role) => (
                              <Badge 
                                key={role} 
                                variant="outline" 
                                className={`text-xs ${isDark ? "border-slate-700" : ""}`}
                              >
                                {ROLE_TYPES[role]?.label || role}
                              </Badge>
                            ))}
                            {(volunteer.role_types || []).length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{volunteer.role_types.length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {renderComplianceBadge(volunteer.compliance?.overall_status)}
                        </TableCell>
                        <TableCell>{volunteer.total_hours || 0}h</TableCell>
                        <TableCell>{volunteer.total_visits || 0}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewVolunteer(volunteer.id)}
                              data-testid={`view-volunteer-${volunteer.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Select onValueChange={(val) => handleUpdateStatus(volunteer.id, val)}>
                              <SelectTrigger className="w-[100px] h-8">
                                <SelectValue placeholder="Status" />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {volunteers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <p className={isDark ? "text-gray-500" : "text-gray-400"}>
                            No volunteers found
                          </p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance" className="space-y-4">
            {/* Compliance Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {Object.entries(complianceTracker?.summary || {}).map(([status, count]) => (
                <Card key={status} className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          {COMPLIANCE_CONFIG[status]?.label || status}
                        </p>
                        <p className="text-2xl font-bold">{count}</p>
                      </div>
                      {COMPLIANCE_CONFIG[status]?.icon && 
                        React.createElement(COMPLIANCE_CONFIG[status].icon, {
                          className: `w-8 h-8 ${COMPLIANCE_CONFIG[status].color}`
                        })
                      }
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Expiring Items Alert */}
            {complianceTracker?.expiring_items?.length > 0 && (
              <Card className={`${isDark ? "bg-slate-900 border-orange-500/30" : "bg-orange-50 border-orange-200"}`}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-orange-500">
                    <AlertTriangle className="w-5 h-5" />
                    Items Expiring Soon
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {complianceTracker.expiring_items.map((item, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg flex items-center justify-between ${isDark ? "bg-slate-800" : "bg-white"}`}
                      >
                        <div>
                          <p className="font-medium">{item.volunteer_name}</p>
                          <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                            {item.item_label}
                          </p>
                        </div>
                        <Badge className={item.is_expired ? "bg-red-500/20 text-red-400" : "bg-yellow-500/20 text-yellow-400"}>
                          {item.is_expired ? "Expired" : `Expires ${item.expiration_date}`}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Compliance Tracker Table */}
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardHeader>
                <CardTitle className="text-lg">Compliance Tracker</CardTitle>
                <CardDescription>
                  Track compliance status for all volunteers
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableHead>Volunteer</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Completion</TableHead>
                      <TableHead>Background</TableHead>
                      <TableHead>TB Test</TableHead>
                      <TableHead>References</TableHead>
                      <TableHead>Orientation</TableHead>
                      <TableHead>HIPAA</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(complianceTracker?.tracker || []).map((vol) => (
                      <TableRow key={vol.volunteer_id} className={isDark ? "border-slate-800" : ""}>
                        <TableCell className="font-medium">{vol.volunteer_name}</TableCell>
                        <TableCell>{renderStatusBadge(vol.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={vol.completion_percentage} className="w-16 h-2" />
                            <span className="text-sm">{vol.completion_percentage}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {vol.items_summary?.background_check === "completed" ? 
                            <CheckCircle className="w-4 h-4 text-green-500" /> : 
                            <Clock className="w-4 h-4 text-gray-400" />
                          }
                        </TableCell>
                        <TableCell>
                          {vol.items_summary?.tb_test === "completed" ? 
                            <CheckCircle className="w-4 h-4 text-green-500" /> : 
                            <Clock className="w-4 h-4 text-gray-400" />
                          }
                        </TableCell>
                        <TableCell>
                          {vol.items_summary?.reference_1 === "completed" && vol.items_summary?.reference_2 === "completed" ? 
                            <CheckCircle className="w-4 h-4 text-green-500" /> : 
                            <Clock className="w-4 h-4 text-gray-400" />
                          }
                        </TableCell>
                        <TableCell>
                          {vol.items_summary?.orientation === "completed" ? 
                            <CheckCircle className="w-4 h-4 text-green-500" /> : 
                            <Clock className="w-4 h-4 text-gray-400" />
                          }
                        </TableCell>
                        <TableCell>
                          {vol.items_summary?.hipaa_training === "completed" ? 
                            <CheckCircle className="w-4 h-4 text-green-500" /> : 
                            <Clock className="w-4 h-4 text-gray-400" />
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments" className="space-y-4">
            <AssignmentsTab 
              isDark={isDark}
              assignments={assignments}
              setAssignmentDialog={setAssignmentDialog}
            />
          </TabsContent>

          {/* Visits Tab */}
          <TabsContent value="visits" className="space-y-4">
            <VisitsTab 
              isDark={isDark}
              visits={visits}
              missedVisits={missedVisits}
              setVisitDialog={setVisitDialog}
            />
          </TabsContent>

          {/* IDT Tab */}
          <TabsContent value="idt" className="space-y-4">
            <IDTSummariesTab 
              isDark={isDark}
              idtSummaries={idtSummaries}
            />
          </TabsContent>

          {/* Programs Tab */}
          <TabsContent value="programs" className="space-y-4">
            <ProgramsTab 
              isDark={isDark}
              specialtyPrograms={specialtyPrograms}
              weeklyCheckins={weeklyCheckins}
            />
          </TabsContent>

          {/* Recruitment Tab */}
          <TabsContent value="recruitment" className="space-y-4">
            <RecruitmentTab 
              isDark={isDark}
              inquiries={inquiries}
              interviews={interviews}
              volunteers={volunteers}
              volunteerRequests={volunteerRequests}
              orientationModules={orientationModules}
              setInquiryDialog={setInquiryDialog}
            />
          </TabsContent>

          {/* 5% Rule Compliance Tab */}
          <TabsContent value="five-percent" className="space-y-4">
            {/* Compliance Status Banner */}
            {fivePercentReport && (
              <Card className={`${
                fivePercentReport.compliance_status?.meets_5_percent_requirement 
                  ? isDark ? "bg-green-500/10 border-green-500/30" : "bg-green-50 border-green-200"
                  : isDark ? "bg-red-500/10 border-red-500/30" : "bg-red-50 border-red-200"
              }`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {fivePercentReport.compliance_status?.meets_5_percent_requirement ? (
                        <CheckCircle className="w-12 h-12 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-12 h-12 text-red-500" />
                      )}
                      <div>
                        <h2 className={`text-2xl font-bold ${
                          fivePercentReport.compliance_status?.meets_5_percent_requirement 
                            ? "text-green-500" : "text-red-500"
                        }`}>
                          {fivePercentReport.compliance_status?.compliance_status}
                        </h2>
                        <p className={isDark ? "text-gray-400" : "text-gray-600"}>
                          CMS 5% Volunteer Rule - {fivePercentReport.compliance_status?.volunteer_percentage}% of patient care hours
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-4xl font-bold">
                        {fivePercentReport.compliance_status?.volunteer_percentage}%
                      </p>
                      <p className={`text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                        Target: ≥5%
                      </p>
                    </div>
                  </div>
                  
                  {!fivePercentReport.compliance_status?.meets_5_percent_requirement && (
                    <div className={`mt-4 p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-white"}`}>
                      <p className="text-red-500 font-medium">
                        {fivePercentReport.gap_analysis?.recommendation}
                      </p>
                      <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                        Need approximately {fivePercentReport.gap_analysis?.additional_visits_needed} more volunteer visits
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Hours Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Volunteer Hours</p>
                      <p className="text-3xl font-bold text-teal-500">
                        {fivePercentReport?.hours_summary?.volunteer_hours || 0}
                      </p>
                    </div>
                    <Users className="w-10 h-10 text-teal-500/30" />
                  </div>
                  <Progress 
                    value={fivePercentReport?.compliance_status?.volunteer_percentage || 0} 
                    className="h-2 mt-3"
                  />
                </CardContent>
              </Card>
              <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Paid Staff Hours</p>
                      <p className="text-3xl font-bold text-blue-500">
                        {fivePercentReport?.hours_summary?.paid_staff_hours || 0}
                      </p>
                    </div>
                    <Clock className="w-10 h-10 text-blue-500/30" />
                  </div>
                </CardContent>
              </Card>
              <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Total Patient Care</p>
                      <p className="text-3xl font-bold text-purple-500">
                        {fivePercentReport?.hours_summary?.total_patient_care_hours || 0}
                      </p>
                    </div>
                    <Activity className="w-10 h-10 text-purple-500/30" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Volunteer Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Volunteer Breakdown */}
              <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                <CardHeader>
                  <CardTitle className="text-lg">Volunteer Contributions</CardTitle>
                  <CardDescription>Hours by volunteer for reporting period</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(fivePercentReport?.volunteer_breakdown || []).map((vol, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                            <span className="text-sm font-medium">{vol.volunteer_name?.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-medium">{vol.volunteer_name}</p>
                            <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                              {vol.visits} visits • {(vol.activities || []).join(", ")}
                            </p>
                          </div>
                        </div>
                        <span className="font-bold text-teal-500">{vol.hours}h</span>
                      </div>
                    ))}
                    {(!fivePercentReport?.volunteer_breakdown || fivePercentReport.volunteer_breakdown.length === 0) && (
                      <p className={`text-center py-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                        No volunteer data for this period
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Service Type Breakdown */}
              <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
                <CardHeader>
                  <CardTitle className="text-lg">Hours by Service Type</CardTitle>
                  <CardDescription>Distribution of volunteer activities</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(fivePercentReport?.service_breakdown || {}).map(([service, data]) => (
                      <div key={service} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                            {ROLE_TYPES[service]?.label || service}
                          </span>
                          <span className="font-medium">{data.hours}h ({data.visits} visits)</span>
                        </div>
                        <Progress 
                          value={(data.hours / (fivePercentReport?.hours_summary?.volunteer_hours || 1)) * 100} 
                          className="h-2"
                        />
                      </div>
                    ))}
                    {Object.keys(fivePercentReport?.service_breakdown || {}).length === 0 && (
                      <p className={`text-center py-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                        No service data for this period
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Report Info */}
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">CMS 5% Volunteer Rule Report</CardTitle>
                  <CardDescription>
                    Report Period: {fivePercentReport?.report_period?.start_date} to {fivePercentReport?.report_period?.end_date}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleExportReport('pdf')}
                    data-testid="export-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleExportReport('excel')}
                    data-testid="export-excel-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Excel
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleExportReport('csv')}
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-blue-50"}`}>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-500" />
                    About the 5% Rule
                  </h4>
                  <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    The CMS Hospice Conditions of Participation require that hospices must use volunteers 
                    to provide day-to-day administrative and/or direct patient care services in an amount 
                    that, at a minimum, equals 5% of the total patient care hours of all paid hospice employees 
                    and contract staff. This is calculated as: Volunteer Hours ÷ (Volunteer Hours + Paid Staff Hours) ≥ 5%
                  </p>
                </div>
                
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-teal-500">
                      {fivePercentReport?.volunteer_metrics?.total_volunteer_visits || 0}
                    </p>
                    <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>Total Visits</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-500">
                      {fivePercentReport?.volunteer_metrics?.unique_volunteers_contributing || 0}
                    </p>
                    <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>Active Volunteers</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-500">
                      {fivePercentReport?.volunteer_metrics?.average_hours_per_visit || 0}h
                    </p>
                    <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>Avg Hours/Visit</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-500">
                      {fivePercentReport?.compliance_status?.volunteer_percentage || 0}%
                    </p>
                    <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>Compliance %</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Alert Configuration */}
            <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Compliance Alerts Configuration
                </CardTitle>
                <CardDescription>
                  Configure automated alerts when compliance drops below threshold
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Alert Threshold</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          defaultValue="5.0"
                          className={`w-24 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                        />
                        <span className={isDark ? "text-gray-400" : "text-gray-600"}>%</span>
                        <span className={`text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                          (Alert when below this)
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Warning Threshold</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          defaultValue="6.0"
                          className={`w-24 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                        />
                        <span className={isDark ? "text-gray-400" : "text-gray-600"}>%</span>
                        <span className={`text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                          (Warning when approaching)
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Alert Recipients</Label>
                      <Input
                        placeholder="admin@goodwincares.com, volunteer.coord@goodwincares.com"
                        className={isDark ? "bg-slate-800 border-slate-700" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox id="weekly-report" defaultChecked />
                        <Label htmlFor="weekly-report" className="text-sm font-normal">
                          Send weekly compliance report
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="immediate-alerts" defaultChecked />
                        <Label htmlFor="immediate-alerts" className="text-sm font-normal">
                          Send alerts immediately when below threshold
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button className="bg-teal-600 hover:bg-teal-700">
                    Save Configuration
                  </Button>
                  <Button variant="outline">
                    Send Test Alert
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Volunteer Dialog */}
      <Dialog open={volunteerDialog} onOpenChange={setVolunteerDialog}>
        <DialogContent className={`max-w-2xl ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
          <DialogHeader>
            <DialogTitle>Add New Volunteer</DialogTitle>
            <DialogDescription>
              Create a new volunteer profile. They will start in Recruiting status.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={volunteerForm.name}
                  onChange={(e) => setVolunteerForm({...volunteerForm, name: e.target.value})}
                  placeholder="John Smith"
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={volunteerForm.email}
                  onChange={(e) => setVolunteerForm({...volunteerForm, email: e.target.value})}
                  placeholder="john@example.com"
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={volunteerForm.phone}
                  onChange={(e) => setVolunteerForm({...volunteerForm, phone: e.target.value})}
                  placeholder="555-123-4567"
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label>Preferred Schedule</Label>
                <Input
                  value={volunteerForm.preferred_schedule}
                  onChange={(e) => setVolunteerForm({...volunteerForm, preferred_schedule: e.target.value})}
                  placeholder="Weekday mornings"
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={volunteerForm.city}
                  onChange={(e) => setVolunteerForm({...volunteerForm, city: e.target.value})}
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input
                  value={volunteerForm.state}
                  onChange={(e) => setVolunteerForm({...volunteerForm, state: e.target.value})}
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label>ZIP Code</Label>
                <Input
                  value={volunteerForm.zip_code}
                  onChange={(e) => setVolunteerForm({...volunteerForm, zip_code: e.target.value})}
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Role Types</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(ROLE_TYPES).map(([key, config]) => (
                  <Badge
                    key={key}
                    variant={volunteerForm.role_types.includes(key) ? "default" : "outline"}
                    className={`cursor-pointer ${volunteerForm.role_types.includes(key) ? "bg-teal-600" : ""}`}
                    onClick={() => {
                      const roles = volunteerForm.role_types.includes(key)
                        ? volunteerForm.role_types.filter(r => r !== key)
                        : [...volunteerForm.role_types, key];
                      setVolunteerForm({...volunteerForm, role_types: roles});
                    }}
                  >
                    {config.label}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Skills (comma separated)</Label>
              <Input
                value={volunteerForm.skills}
                onChange={(e) => setVolunteerForm({...volunteerForm, skills: e.target.value})}
                placeholder="reading, music, conversation"
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label>Languages (comma separated)</Label>
              <Input
                value={volunteerForm.languages}
                onChange={(e) => setVolunteerForm({...volunteerForm, languages: e.target.value})}
                placeholder="english, spanish"
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Emergency Contact Name</Label>
                <Input
                  value={volunteerForm.emergency_contact_name}
                  onChange={(e) => setVolunteerForm({...volunteerForm, emergency_contact_name: e.target.value})}
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label>Emergency Contact Phone</Label>
                <Input
                  value={volunteerForm.emergency_contact_phone}
                  onChange={(e) => setVolunteerForm({...volunteerForm, emergency_contact_phone: e.target.value})}
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={volunteerForm.notes}
                onChange={(e) => setVolunteerForm({...volunteerForm, notes: e.target.value})}
                placeholder="Additional notes about this volunteer..."
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVolunteerDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateVolunteer}
              disabled={!volunteerForm.name}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Create Volunteer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Volunteer Detail Dialog */}
      <Dialog open={volunteerDetailDialog} onOpenChange={setVolunteerDetailDialog}>
        <DialogContent className={`max-w-2xl ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
          <DialogHeader>
            <DialogTitle>{selectedVolunteer?.name}</DialogTitle>
            <DialogDescription>
              Volunteer Profile Details
            </DialogDescription>
          </DialogHeader>
          {selectedVolunteer && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <div className="mt-1">{renderStatusBadge(selectedVolunteer.status)}</div>
                </div>
                <div>
                  <Label className="text-gray-500">Compliance</Label>
                  <div className="mt-1">{renderComplianceBadge(selectedVolunteer.compliance?.overall_status)}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Email</Label>
                  <p>{selectedVolunteer.email || "Not provided"}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Phone</Label>
                  <p>{selectedVolunteer.phone || "Not provided"}</p>
                </div>
              </div>
              <div>
                <Label className="text-gray-500">Role Types</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {(selectedVolunteer.role_types || []).map((role) => (
                    <Badge key={role} variant="outline">
                      {ROLE_TYPES[role]?.label || role}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Total Hours</Label>
                  <p className="text-2xl font-bold text-teal-500">{selectedVolunteer.total_hours || 0}h</p>
                </div>
                <div>
                  <Label className="text-gray-500">Total Visits</Label>
                  <p className="text-2xl font-bold text-blue-500">{selectedVolunteer.total_visits || 0}</p>
                </div>
              </div>
              
              {/* Compliance Items */}
              <div>
                <Label className="text-gray-500 mb-2 block">Compliance Items</Label>
                <div className="space-y-2">
                  {(selectedVolunteer.compliance?.items || []).map((item, idx) => (
                    <div 
                      key={idx}
                      className={`p-2 rounded flex items-center justify-between ${isDark ? "bg-slate-800" : "bg-gray-50"}`}
                    >
                      <span>{item.label || item.item_type}</span>
                      {item.status === "completed" ? 
                        <CheckCircle className="w-4 h-4 text-green-500" /> : 
                        <Clock className="w-4 h-4 text-gray-400" />
                      }
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVolunteerDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Visit Dialog */}
      <Dialog open={visitDialog} onOpenChange={setVisitDialog}>
        <DialogContent className={`max-w-lg ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
          <DialogHeader>
            <DialogTitle>Log Volunteer Visit</DialogTitle>
            <DialogDescription>
              Record a volunteer visit with a patient
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Volunteer</Label>
              <Select 
                value={visitForm.volunteer_id} 
                onValueChange={(val) => setVisitForm({...visitForm, volunteer_id: val})}
              >
                <SelectTrigger className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                  <SelectValue placeholder="Select volunteer" />
                </SelectTrigger>
                <SelectContent>
                  {volunteers.filter(v => v.status === "active").map((vol) => (
                    <SelectItem key={vol.id} value={vol.id}>{vol.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Patient ID</Label>
              <Input
                value={visitForm.patient_id}
                onChange={(e) => setVisitForm({...visitForm, patient_id: e.target.value})}
                placeholder="Patient ID"
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Visit Date</Label>
                <Input
                  type="date"
                  value={visitForm.visit_date}
                  onChange={(e) => setVisitForm({...visitForm, visit_date: e.target.value})}
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={visitForm.duration_minutes}
                  onChange={(e) => setVisitForm({...visitForm, duration_minutes: parseInt(e.target.value)})}
                  className={isDark ? "bg-slate-800 border-slate-700" : ""}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Observations</Label>
              <Textarea
                value={visitForm.observations}
                onChange={(e) => setVisitForm({...visitForm, observations: e.target.value})}
                placeholder="Patient observations during visit..."
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={visitForm.notes}
                onChange={(e) => setVisitForm({...visitForm, notes: e.target.value})}
                placeholder="Additional notes..."
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVisitDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleLogVisit}
              disabled={!visitForm.volunteer_id || !visitForm.patient_id}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Log Visit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
